import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, animate, useMotionValue } from "framer-motion";
import { Play, Video, Mic } from "lucide-react";

/**
 * Self-playing, looping demo for "Real-Time Audio & Visual Tracking".
 * Phase 1 (vision): a camera detects a knee, boxes it, reads the flexion angle,
 * and auto-fills the objective table. Phase 2 (audio): the transcription sidecar
 * hears spoken callouts and fills the remaining measurements. No user interaction.
 */

type Cell = "RF" | "LF" | "RE" | "LE";

const VALUES: Record<Cell, number> = { RF: 142, LF: 138, RE: 3, LE: 5 };

const CALLOUTS: { cell: Cell; text: string }[] = [
  { cell: "RE", text: "Right knee extension" },
  { cell: "LE", text: "Left knee extension" },
];

interface Frame {
  phase: "vision" | "audio";
  filled: Cell[];
  /** how many audio callouts are visible */
  callouts: number;
}

const FRAMES: Frame[] = [
  { phase: "vision", filled: [], callouts: 0 },
  { phase: "vision", filled: ["RF"], callouts: 0 },
  { phase: "vision", filled: ["RF", "LF"], callouts: 0 },
  { phase: "audio", filled: ["RF", "LF"], callouts: 0 },
  { phase: "audio", filled: ["RF", "LF", "RE"], callouts: 1 },
  { phase: "audio", filled: ["RF", "LF", "RE", "LE"], callouts: 2 },
];

const STEP_MS = 1100;
const HOLD_MS = 2200;

function AnimatedNumber({ value, suffix = "" }: { value: number; suffix?: string }) {
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const controls = animate(mv, value, {
      duration: 0.8,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
  }, [value, mv]);
  return (
    <>
      {display}
      {suffix}
    </>
  );
}

export default function ObjectiveTrackingWidget() {
  const [frame, setFrame] = useState(0);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setFrame(FRAMES.length - 1);
      return;
    }
    const isLast = frame >= FRAMES.length - 1;
    timeoutRef.current = window.setTimeout(
      () => setFrame((f) => (f >= FRAMES.length - 1 ? 0 : f + 1)),
      isLast ? HOLD_MS : STEP_MS
    );
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [frame]);

  const { phase, filled, callouts } = FRAMES[frame];
  const isFilled = (cell: Cell) => filled.includes(cell);

  const ROWS: { label: string; right: Cell; left: Cell }[] = [
    { label: "Flexion", right: "RF", left: "LF" },
    { label: "Extension", right: "RE", left: "LE" },
  ];

  return (
    <div className="relative w-full h-full font-productSans overflow-hidden">
      {/* Left — swaps between camera (vision) and transcription (audio) */}
      <div className="absolute left-0 top-[4%] bottom-[4%] w-[52%] sm:w-[55%] rounded-[20px] sm:rounded-[24px] bg-white shadow-[0_20px_56px_-18px_rgba(31,61,59,0.42)] overflow-hidden z-10">
        <AnimatePresence mode="wait">
          {phase === "vision" ? (
            <motion.div
              key="camera"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="w-full h-full relative bg-[#0f1a19]"
            >
              <img
                src="/notation/knee.png"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />

              {/* Connection status */}
              <div className="absolute top-2 left-2 sm:top-2.5 sm:left-2.5 flex items-center gap-1 sm:gap-1.5 rounded-full bg-black/45 backdrop-blur px-1.5 sm:px-2 py-1 max-w-[70%]">
                <motion.span
                  className="w-1.5 h-1.5 shrink-0 rounded-full bg-[#22c55e]"
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ duration: 1.4, repeat: Infinity }}
                />
                <span className="text-[7.5px] sm:text-[8.5px] font-medium text-white truncate">
                  Connection established
                </span>
              </div>
              <div className="absolute top-2 right-2 sm:top-2.5 sm:right-2.5 rounded-full bg-black/45 backdrop-blur p-1.5">
                <Video className="w-3 h-3 text-white" />
              </div>

              {/* Bounding box on the knee */}
              <AnimatePresence>
                {filled.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute"
                    style={{ left: "38%", top: "10%", width: "56%", height: "72%" }}
                  >
                    <div className="w-full h-full border-2 border-[#22c55e] rounded-md relative">
                      {[
                        "top-0 left-0 border-t-2 border-l-2",
                        "top-0 right-0 border-t-2 border-r-2",
                        "bottom-0 left-0 border-b-2 border-l-2",
                        "bottom-0 right-0 border-b-2 border-r-2",
                      ].map((pos) => (
                        <span
                          key={pos}
                          className={`absolute w-2.5 h-2.5 border-white ${pos}`}
                        />
                      ))}
                      <span className="absolute -top-[18px] left-0 bg-[#22c55e] text-white text-[8px] font-semibold px-1.5 py-0.5 rounded">
                        Knee
                      </span>
                      <div className="absolute bottom-1.5 right-1.5 bg-black/60 backdrop-blur rounded px-1.5 py-0.5">
                        <span className="text-[#7DD3FC] text-[11px] font-bold">
                          <AnimatedNumber value={VALUES.RF} suffix="°" />
                        </span>
                        <span className="text-white/70 text-[7.5px] ml-0.5">
                          flexion
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1.5 rounded-full bg-[#1F3D3B]/80 px-2 py-1">
                <span className="text-[8.5px] font-medium text-white">
                  Motion tracking
                </span>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="transcript"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="w-full h-full flex flex-col"
            >
              <div className="flex items-center justify-between px-3 py-2.5 bg-[#1F3D3B]">
                <span className="text-[11px] font-semibold text-white">Transcript</span>
                <span className="flex items-center gap-1.5 text-[9px] font-medium text-white/80">
                  <Mic className="w-3 h-3" />
                  <span className="flex items-end gap-[2px] h-3">
                    {[0, 1, 2, 3].map((i) => (
                      <motion.span
                        key={i}
                        className="w-[3px] rounded-full bg-[#ef4444]"
                        animate={{ height: ["30%", "100%", "45%"] }}
                        transition={{
                          duration: 0.7,
                          repeat: Infinity,
                          delay: i * 0.12,
                          ease: "easeInOut",
                        }}
                        style={{ height: "30%" }}
                      />
                    ))}
                  </span>
                  Transcribing
                </span>
              </div>
              <div className="flex-1 min-h-0 px-3 py-3 flex flex-col justify-start gap-2.5 overflow-hidden">
                <AnimatePresence initial={false}>
                  {CALLOUTS.slice(0, callouts).map((co) => (
                    <motion.div
                      key={co.cell}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="shrink-0"
                    >
                      <p className="text-[9px] font-semibold mb-0.5 text-[#439CCB]">
                        Clinician
                      </p>
                      <p className="text-[11px] leading-snug text-gray-700">
                        {co.text} is {VALUES[co.cell]} degrees.
                      </p>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Right — objective measurements table */}
      <div className="absolute right-0 top-[10%] bottom-[10%] w-[50%] sm:w-[48%] rounded-[20px] sm:rounded-[24px] bg-white shadow-[0_24px_64px_-18px_rgba(31,61,59,0.45)] overflow-hidden flex flex-col z-20">
        <div className="flex items-center justify-between px-2.5 sm:px-3.5 py-2 sm:py-2.5 bg-[#1F3D3B]">
          <span className="text-[11px] sm:text-[12px] font-semibold text-white">Objective</span>
          <span className="text-[8px] sm:text-[8.5px] text-white/70">Knee AROM</span>
        </div>

        <div className="flex-1 min-w-0 px-2 sm:px-3 py-2.5 sm:py-3 overflow-hidden">
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(40px,auto)_minmax(40px,auto)] gap-1 sm:gap-2 items-center pb-1.5 mb-1.5 border-b border-gray-100">
            <span className="text-[8px] sm:text-[9px] font-semibold text-gray-400 truncate">
              Measurement
            </span>
            <span className="text-[8px] sm:text-[9px] font-semibold text-gray-400 text-center">
              Right
            </span>
            <span className="text-[8px] sm:text-[9px] font-semibold text-gray-400 text-center">
              Left
            </span>
          </div>

          {ROWS.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-[minmax(0,1fr)_minmax(40px,auto)_minmax(40px,auto)] gap-1 sm:gap-2 items-center py-1 sm:py-1.5"
            >
              <span className="text-[9px] sm:text-[10.5px] font-medium text-gray-700 truncate">
                {row.label}
              </span>
              {[row.right, row.left].map((cell) => (
                <TableCell key={cell} filled={isFilled(cell)} value={VALUES[cell]} />
              ))}
            </div>
          ))}

          <p className="mt-2 sm:mt-3 text-[7.5px] sm:text-[8.5px] text-gray-400 leading-snug">
            Captured from camera &amp; spoken callouts — no manual entry.
          </p>
        </div>
      </div>
    </div>
  );
}

function TableCell({ filled, value }: { filled: boolean; value: number }) {
  return (
    <div
      className={`w-[40px] sm:w-[52px] h-[22px] sm:h-[24px] rounded-md flex items-center justify-center gap-0.5 sm:gap-1 transition-colors duration-300 ${
        filled ? "bg-[#f6faf7] ring-1 ring-[#486633]/30" : "bg-gray-50"
      }`}
    >
      {filled ? (
        <motion.span
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-0.5"
        >
          <span className="text-[9px] sm:text-[11px] font-bold text-gray-900">
            <AnimatedNumber value={value} suffix="°" />
          </span>
          <Play className="w-1.5 h-1.5 sm:w-2 sm:h-2 text-[#486633] fill-[#486633]" />
        </motion.span>
      ) : (
        <span className="w-3 sm:w-4 h-1 rounded bg-gray-200" />
      )}
    </div>
  );
}
